import API, { updateLastActivity, refreshToken as apiRefreshToken } from './api';
import { handleApiError } from './utilsService';
import { TOKEN_EXPIRATION, INACTIVITY_TIMEOUT, API_URL } from '../config';
import { debug, info, warn, error } from '../utils/logger';

// Constantes para el almacenamiento local
const USER_KEY = 'prokids_user';
const TOKEN_KEY = 'prokids_token';
const REFRESH_TOKEN_KEY = 'prokids_refresh_token';
const TOKEN_EXPIRY_KEY = 'prokids_token_expiry';
const LAST_ACTIVITY_KEY = 'prokids_last_activity';
// Eliminadas las constantes de bloqueo - ahora se maneja en el backend

// Servicio para la autenticación
const authService = {
  // Registrar un nuevo usuario
  register: async (userData) => {
    try {
      // Hacer la petición al servidor
      const response = await API.post('/auth/register', userData);

      if (response.data) {
        const { user, token, refreshToken } = response.data;

        // Guardar datos en localStorage
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        localStorage.setItem(TOKEN_KEY, token);

        // Guardar el token de refresco si está disponible
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }

        // Establecer el tiempo de expiración del token
        const expiryTime = new Date().getTime() + TOKEN_EXPIRATION;
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

        // Actualizar la última actividad
        updateLastActivity();
      }

      return response.data;
    } catch (error) {
      console.log('Error en authService.register:', error);

      // Si el error ya fue procesado por el interceptor de axios (tiene type y message)
      if (error.type && error.message) {
        const apiError = new Error(error.message);
        // Preservar la información original si existe
        if (error.originalError && error.originalError.response) {
          apiError.response = error.originalError.response;
        }
        throw apiError;
      }

      // Si el error tiene una respuesta del servidor (error de axios sin procesar)
      if (error.response && error.response.data && error.response.data.message) {
        const apiError = new Error(error.response.data.message);
        apiError.response = error.response;
        throw apiError;
      }

      throw handleApiError(error, 'Error al registrar el usuario');
    }
  },

  // Verificar email
  verifyEmail: async (token) => {
    try {
      const response = await API.post('/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al verificar el email');
    }
  },

  // Reenviar email de verificación
  resendVerificationEmail: async (email) => {
    try {
      const response = await API.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al reenviar el email de verificación');
    }
  },

  // Iniciar sesión
  login: async (credentials, rememberMe = false) => {
    try {
      debug('Iniciando proceso de login');

      // Extraer remember de las credenciales si existe
      if (credentials.remember !== undefined) {
        rememberMe = credentials.remember;
        // Crear una copia de las credenciales sin el campo remember
        const { remember: _remember, ...credentialsWithoutRemember } = credentials;
        credentials = credentialsWithoutRemember;
      }

      debug('Enviando petición de login al servidor', { apiUrl: API_URL });

      // Hacer la petición al servidor
      try {
        // Intentar primero con la ruta /api/auth/login
        const response = await API.post('/auth/login', credentials);
        debug('Respuesta del servidor recibida', { status: response.status });

        if (response.data) {
          debug('Datos de respuesta válidos');
          const { user, token, refreshToken } = response.data;

          if (!user) {
            error('No se recibió información del usuario en la respuesta');
            throw new Error('No se recibió información del usuario');
          }

          if (!token) {
            error('No se recibió token en la respuesta');
            throw new Error('No se recibió token de autenticación');
          }

          debug('Guardando datos en localStorage');
          // Guardar datos en localStorage
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          localStorage.setItem(TOKEN_KEY, token);

          // Guardar el token de refresco si está disponible
          if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
          }

          // Establecer el tiempo de expiración del token
          // Si rememberMe es true, el token durará más tiempo
          const expiryTime =
            new Date().getTime() + (rememberMe ? TOKEN_EXPIRATION * 7 : TOKEN_EXPIRATION);
          localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

          // Actualizar la última actividad
          updateLastActivity();

          info('Login completado con éxito');
          return response.data;
        } else {
          error('Respuesta sin datos');
          throw new Error('No se recibieron datos del servidor');
        }
      } catch (apiError) {
        error('Error en la petición de login', { error: apiError.message });

        // Si el error es de conexión o timeout, mostrar un mensaje más amigable
        if (
          apiError.message &&
          (apiError.message.includes('Network Error') || apiError.message.includes('timeout'))
        ) {
          error('Error de conexión al servidor');
          throw new Error(
            'No se pudo conectar al servidor. Por favor, verifica tu conexión a internet e inténtalo de nuevo.',
          );
        }

        // Si el error es 401, mostrar mensaje de credenciales incorrectas
        if (apiError.response && apiError.response.status === 401) {
          throw new Error('Credenciales incorrectas. Por favor, verifica tu email y contraseña.');
        }

        // Para otros errores, lanzar el error original
        throw apiError;
      }
    } catch (loginError) {
      error('Error en authService.login', { error: loginError.message });

      // El backend ahora maneja el bloqueo por intentos fallidos

      throw handleApiError(loginError, 'Error al iniciar sesión');
    }
  },

  // Cerrar sesión
  logout: async () => {
    try {
      // Intentar hacer logout en el servidor si hay un token
      const token = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (token) {
        await API.post('/auth/logout', { refreshToken });
      }
    } catch (logoutError) {
      warn('Error al cerrar sesión en el servidor', { error: logoutError.message });
    } finally {
      // Siempre limpiar el almacenamiento local
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    }
  },

  // Obtener el usuario actual
  getCurrentUser: () => {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (parseError) {
      error('Error al parsear el usuario', { error: parseError.message });
      // Si hay un error al parsear, limpiar el almacenamiento local
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);

    // Debe haber tanto token como usuario
    if (!token || !user) return false;

    // Verificar si el token ha expirado
    if (authService.isTokenExpired()) {
      // Si el token ha expirado pero hay un token de refresco, consideramos que está autenticado
      // La renovación se manejará automáticamente en el interceptor de API
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        debug('Token expirado pero hay refresh token disponible');
        return true;
      }

      // Si no hay token de refresco, limpiar el almacenamiento local
      debug('Token expirado y sin refresh token, limpiando autenticación');
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return false;
    }

    return true;
  },

  // Verificar si el usuario tiene un rol específico
  hasRole: (role) => {
    const user = authService.getCurrentUser();
    if (!user) return false;

    // Si role es un array, verificar si el usuario tiene alguno de los roles
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }

    // Si role es un string, verificar si el usuario tiene ese rol
    return user.role === role;
  },

  // Obtener el perfil del usuario
  getProfile: async () => {
    try {
      const response = await API.get('/users/profile');

      // Actualizar el usuario en el almacenamiento local
      if (response.data && response.data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener el perfil del usuario');
    }
  },

  // Cambiar la contraseña del usuario
  changePassword: async (passwordData) => {
    try {
      // La ruta real es /auth/change-password; /users/password no existe en el backend
      const response = await API.put('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al cambiar la contraseña');
    }
  },

  // Solicitar restablecimiento de contraseña
  requestPasswordReset: async (email) => {
    try {
      const response = await API.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al solicitar el restablecimiento de contraseña');
    }
  },

  // Restablecer contraseña con token
  resetPassword: async (token, newPassword) => {
    try {
      const response = await API.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al restablecer la contraseña');
    }
  },

  // Refrescar el token de acceso
  refreshToken: async () => {
    try {
      return await apiRefreshToken();
    } catch (error) {
      throw handleApiError(error, 'Error al refrescar el token');
    }
  },

  // Verificar el token de acceso
  verifyToken: async () => {
    try {
      const response = await API.get('/auth/verify-token');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Token inválido o expirado');
    }
  },

  // Obtener el token actual
  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Verificar si el token ha expirado
  isTokenExpired: () => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return true;
    return new Date().getTime() > parseInt(expiryTime);
  },

  // Actualizar la última actividad del usuario
  updateLastActivity: () => {
    updateLastActivity();
  },

  // Métodos de bloqueo eliminados - ahora se maneja en el backend

  // Verificar si el usuario ha estado inactivo por demasiado tiempo
  checkInactivity: () => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return false;

    const inactiveTime = new Date().getTime() - parseInt(lastActivity);
    return inactiveTime > INACTIVITY_TIMEOUT;
  },

  // Limpiar todos los datos de autenticación
  clearAuth: () => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  },
};

export default authService;
